<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5"> 
      <TableTitle
        :title="title"
        pageTitle="Process Group"
        icon="fas fa-tasks"
        :showAction="true"
        actionLabel="Create New"
        modal="createNew"
        :disabled="!canCreate('main')"
      />
      <CommonTable2
        :tableObj="tableConfig" 
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="main"
      >
        <template #extra-actions="{ row }">
          <button
            class="btn btn-sm btn-outline-info"
            title="Design Flow"
            @click="openFlowEditor(row)"
          >
            <i class="fas fa-project-diagram"></i>
          </button>
        </template>
      </CommonTable2>
    </div>
  </div>

  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 px-3">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
    saveButtonText="Create"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>

  <!-- Process Flow Editor Component -->
  <ProcessFlowEditor
    v-if="isFlowEditorOpen"
    :process-title="selectedProcess?.title"
    :initial-data="selectedProcess?.raw_object?.flow_diagram"
    @close="closeFlowEditor"
    @save="saveFlow"
  /> 
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue"; 
import { usePermissions } from "@/composables/usePermissions";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import apiService from "@/services/apiService";
import { toast, type Content } from "vue3-toastify";
import ProcessFlowEditor from "./ProcessFlowEditor.vue";

const { canCreate } = usePermissions();
const API_RESOURCE = "cms-process-flow/process-transitions";
const pageTitle = "Process Transition";
const title = "Default Processes";

const crud = useCrud(API_RESOURCE);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [1];
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});

const rows = ref<any[]>([]);
const cardTypes = ref<any[]>([]);
const processGroups = ref<any[]>([]);

const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Card Type", field: "type_value", sortable: true, width: "auto" }
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",  
});

watch(crud.data, (newVal) => rows.value = newVal || []);
watch(crud.isLoading, (v) => tableConfig.value.loading = v);
watch(() => crud.pagination.value, (newVal) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { immediate: true, deep: true });

const changeRows = () => {
  currentPage.value = 1;
  crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};
const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) return;
  currentPage.value = page;
  crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);
const isFlowEditorOpen = ref(false);
const selectedProcess = ref<any>(null);

const newItemData = ref({ 
  group_id: "", 
  title: "", 
  variable_type: "card_type", 
  type_value: "" 
});

const editingItemData = ref<any>({ 
  _id: "", 
  group_id: "", 
  title: "", 
  variable_type: "card_type", 
  type_value: "" 
});

const fetchOptions = async () => {
  try {
    const [cardRes, groupRes] = await Promise.all([
      apiService.get("common/cardtypes-filtered", { all: true, default: true }), // Changed to fetch all
      apiService.get("cms-process-flow/process-groups")
    ]);
    
    cardTypes.value = cardRes.data || [];
    processGroups.value = groupRes.data || groupRes || [];
    
    // Pre-selection logic for group_id
    if (processGroups.value.length > 0) {
      const defaultGroup = processGroups.value.find((g: any) => g.title?.toLowerCase().includes('default')) || processGroups.value[0];
      if (defaultGroup) {
        newItemData.value.group_id = defaultGroup._id;
      }
    }
  } catch (err) {
    console.error("Failed to fetch options", err);
  }
};

const createFormFields = computed<FormField[]>(() => [
  { 
    key: "group_id", 
    label: "Select Group", 
    type: "select", 
    required: true, 
    options: processGroups.value.map(g => ({ _id: g._id, title: g.title }))
  },
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title" },
  { 
    key: "type_value", 
    label: "Card Type", 
    type: "select", 
    required: true, 
    options: cardTypes.value.map(c => ({ _id: c.title, title: c.title }))
  },
  { key: "variable_type", label: "Variable Type", type: "text", required: true },
]);

const editFormFields = computed<FormField[]>(() => [
  { 
    key: "group_id", 
    label: "Select Group", 
    type: "select", 
    required: true, 
    options: processGroups.value.map(g => ({ _id: g._id, title: g.title })),
    readonly: isViewMode.value 
  },
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title", readonly: isViewMode.value },
  { 
    key: "type_value", 
    label: "Card Type", 
    type: "select", 
    required: true, 
    options: cardTypes.value.map(c => ({ _id: c.title, title: c.title })),
    readonly: isViewMode.value 
  },
  { key: "variable_type", label: "Variable Type", type: "text", required: true, readonly: isViewMode.value },
]);

const isCreateFormValid = computed(() => !newItemData.value.title.trim() || !newItemData.value.group_id || !newItemData.value.type_value);
const isEditFormValid = computed(() => !editingItemData.value.title?.trim() || !editingItemData.value.group_id || !editingItemData.value.type_value);

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure?")) {
    await crud.deleteItem(id);
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};
const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const res = await crud.createItem(newItemData.value);
  if (res) {
    isCreateModalOpen.value = false;
    newItemData.value = { 
      group_id: processGroups.value.find((g: any) => g.title?.toLowerCase().includes('default'))?._id || processGroups.value[0]?._id || "", 
      title: "", 
      variable_type: "card_type", 
      type_value: "" 
    };
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};
const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...payload } = editingItemData.value;
  try {
    crud.isLoading.value = true;
    const res = await apiService.put(API_RESOURCE, id, payload);
    if (res) {
      toast.success("Item updated successfully" as Content, { theme: "colored" });
      isEditModalOpen.value = false;
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error((err.response?.data?.message || err.message || "Failed to update.") as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};
const closeCreateModal = () => { 
  isCreateModalOpen.value = false; 
  newItemData.value = { 
    group_id: processGroups.value.find((g: any) => g.title?.toLowerCase().includes('default'))?._id || processGroups.value[0]?._id || "", 
    title: "", 
    variable_type: "card_type", 
    type_value: "" 
  }; 
};
const closeEditModal = () => { isEditModalOpen.value = false; isViewMode.value = false; editingItemData.value = { _id: "", group_id: "", title: "", variable_type: "card_type", type_value: "" }; };

const openFlowEditor = (row: any) => {
  selectedProcess.value = row;
  isFlowEditorOpen.value = true;
  console.log(selectedProcess.value, 'selectedProcess.value');
};


const closeFlowEditor = () => {
  isFlowEditorOpen.value = false;
  selectedProcess.value = null;
};

const saveFlow = async (flowObject: any) => {
  if (!selectedProcess.value?._id) return;
  
  try {
    crud.isLoading.value = true;
    const payload = {
      ...selectedProcess.value,
      raw_object: {
        ...(selectedProcess.value.raw_object || {}),
        flow_diagram: flowObject
      }
    };
    
    // Remove _id from payload for PUT
    const { _id, ...cleanPayload } = payload;
    
    const res = await apiService.put(API_RESOURCE, selectedProcess.value._id, cleanPayload);
    if (res) {
      toast.success("Workflow saved successfully" as Content, { theme: "colored" });
      closeFlowEditor();
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error("Failed to save workflow" as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([
    crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page }),
    fetchOptions()
  ]);
});
</script>

<style scoped>
.rows-select { width: 82px; padding: 8px 17px; font-size: 0.85rem; background-color: transparent; }
.pagination-mini { display: flex; align-items: center; border-radius: 6px; overflow: hidden; }
.page-btn { border: none; background: transparent; font-size: 1rem; padding: 7px 12px; cursor: pointer; color: #6c757d; transition: 0.2s; border: 1px solid #dee2e6; outline: none; }
.page-btn:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }
.page-btn:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active { background: #21c1d6; color: #fff; padding: 7px 12px; font-weight: 600; min-width: 28px; text-align: center; display: inline-block; border: 1px solid #21c1d6; }
.prev_btn, .next_btn { color: #21c1d6; }
</style>
